import TrendingUpIcon from "@redotech/redo-icons/line-chart-up-02.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { type CSSProperties, memo } from "react";

import * as css from "./login-hero-panel.module.css";

const HERO_IMAGE_URL =
  "https://assets.getredo.com/merchant/login-hero-20260401.webp";

// Newsletter open-rate visualization. Twelve bars with eight filled =
// ~67%, matching the "67% open rate" copy below. Pulled out as
// constants so the visual ratio and the metric line can never drift.
const NEWSLETTER_BARS = 12;
const NEWSLETTER_BARS_FILLED = 8;

// Payouts card cycles through these windows so the value moves while
// the rest of the card stays still. CSS handles the timing — three
// synchronized animations on a shared 12s beat: the metric track
// rolls vertically, each delta frame cross-fades on its own
// nth-child keyframe, and the sparkline morphs its `d` attribute.
// Order matters: the metric track expects the duplicate frame at
// the END of the iteration, and deltaCycle{1..4} keyframes assume
// frame 1 is the first child.
const PAYOUT_FRAMES: readonly {
  amount: string;
  delta: string;
  window: string;
}[] = [
  { amount: "$1,847", delta: "12%", window: "today" },
  { amount: "$11,420", delta: "18%", window: "7 days" },
  { amount: "$48,210", delta: "23%", window: "30 days" },
  { amount: "$128,940", delta: "31%", window: "90 days" },
];

export const LoginHeroPanel = memo(function LoginHeroPanel() {
  return (
    <div className={css.panel}>
      <img alt="" aria-hidden className={css.heroImage} src={HERO_IMAGE_URL} />
      <div className={css.overlay} />

      {/* Decorative metric cards. aria-hidden because the numbers are
          mocks for visual storytelling — screen readers should land
          on the headline copy instead. */}
      <div aria-hidden className={css.cards}>
        <article className={`${css.card} ${css.cardLive}`}>
          <header className={css.cardLabel}>
            <span className={css.livePulse} />
            Live now
          </header>
          <div className={css.metric}>2,341</div>
          <div className={css.metricSub}>viewers</div>
          <span className={css.cardSubject}>Lululemon × Sarah Ross</span>
        </article>

        <article className={`${css.card} ${css.cardPayouts}`}>
          <header className={css.cardLabel}>Payouts</header>

          {/* Metric: vertical odometer roll. Track has 4 amounts +
              a duplicate of the first appended so the loop wraps
              without a visible snap. */}
          <div className={css.metricRotator}>
            <div className={css.metricTrack}>
              {[...PAYOUT_FRAMES, PAYOUT_FRAMES[0]!].map((frame, i) => (
                <div className={css.metricRow} key={`m-${frame.window}-${i}`}>
                  {frame.amount}
                </div>
              ))}
            </div>
          </div>

          {/* Delta + window: cross-fade. Each frame has its own
              keyframe with a visibility window aligned to its slot. */}
          <div className={css.deltaRotator}>
            {PAYOUT_FRAMES.map((frame) => (
              <span className={css.deltaFrame} key={`d-${frame.window}`}>
                <RedoIcon
                  className={css.deltaArrow}
                  Icon={TrendingUpIcon}
                  size={12}
                />
                {frame.delta} · {frame.window}
              </span>
            ))}
          </div>

          {/* Sparkline: a tinted area fill under a single morphing
              line. Both shapes share the same 12s morph beat. The
              `d=` attributes below are the static fallbacks for
              browsers without CSS-animated `d` (both render the
              30-day shape). */}
          <svg
            className={css.sparkline}
            preserveAspectRatio="none"
            viewBox="0 0 200 30"
          >
            <defs>
              <linearGradient
                gradientUnits="userSpaceOnUse"
                id="sparklineAreaGradient"
                x1="0"
                x2="0"
                y1="0"
                y2="30"
              >
                <stop offset="0%" stopColor="#5eead4" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#5eead4" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              className={css.sparklineArea}
              d="M 0 24 L 22 19 L 44 21 L 66 14 L 88 16 L 110 10 L 132 13 L 154 6 L 176 8 L 200 3 L 200 30 L 0 30 Z"
            />
            <path
              className={css.sparklinePath}
              d="M 0 24 L 22 19 L 44 21 L 66 14 L 88 16 L 110 10 L 132 13 L 154 6 L 176 8 L 200 3"
            />
          </svg>
        </article>

        <article className={`${css.card} ${css.cardNewsletter}`}>
          <header className={css.cardLabel}>Newsletter</header>
          <div className={css.metric}>38,492</div>
          <div className={css.metricSub}>sent · 67% open rate</div>
          <div className={css.bars}>
            {Array.from({ length: NEWSLETTER_BARS }).map((_, i) => (
              <span
                className={`${css.bar} ${i < NEWSLETTER_BARS_FILLED ? css.barFilled : ""}`}
                key={i}
                style={{ "--bar-i": i } as CSSProperties}
              />
            ))}
          </div>
        </article>
      </div>

      <div className={css.content}>
        <div className={css.headline}>
          <span className={css.eyebrow}>Redo Partners</span>
          <p className={css.tagline}>
            Refer brands to Redo. Track performance, payouts, and your customers
            — all in one place.
          </p>
        </div>
      </div>
    </div>
  );
});
